import React, { useState, useEffect } from "react";

import Navbar from "../components/Navbar";
import Sidebar from "../components/Sidebar";
import { fetchRegistrationRequests, approveRegistrationRequest, rejectRegistrationRequest, fetchAssignedPlots } from "../api/api";

const STATUS_LABELS = {
  pending: "Pending",
  approved: "Approved",
  rejected: "Rejected",
};

const STATUS_CLASS = {
  pending: "status-open",
  approved: "status-closed",
  rejected: "status-progress",
};

function errorMessage(err) {
  return err.response?.data?.detail || "Something went wrong. Please try again.";
}

// Search-and-pick UI for overriding which plot(s) get assigned, in case
// the automatic match against what the customer typed is wrong/missing
// (typo, or a plot that exists under a slightly different number) — and/or
// to assign more than one plot at once, e.g. a customer's plots were split
// from one comma-separated Zoho row (1A/1B/1C) and all of them, not just
// the one claimed, should be linked to this account. `allPlots` is fetched
// once, same pattern as EstateUpdates.js's CustomerPicker.
function PlotPicker({ allPlots, selectedPlots, onAdd, onRemove }) {
  const [query, setQuery] = useState("");

  const selectedIds = new Set(selectedPlots.map((p) => p.id));
  const matches = query.trim()
    ? allPlots
        .filter((p) => {
          const q = query.trim().toLowerCase();
          return !selectedIds.has(p.id) && (p.plot_number?.toLowerCase().includes(q) || p.owner_name?.toLowerCase().includes(q));
        })
        .slice(0, 8)
    : [];

  return (
    <div style={{ minWidth: "220px" }}>
      {selectedPlots.map((p) => (
        <div key={p.id} style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "6px" }}>
          <span>
            Plot <strong>{p.plot_number}</strong>
            {p.owner_name ? ` — currently owned by ${p.owner_name}` : " — unowned"}
          </span>
          <button type="button" onClick={() => onRemove(p.id)}>Remove</button>
        </div>
      ))}
      <input
        type="text"
        placeholder={selectedPlots.length ? "Add another plot..." : "Search plot number or current owner..."}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        style={{ width: "100%" }}
      />
      {matches.length > 0 && (
        <div style={{ border: "1px solid #ddd", borderRadius: "6px", marginTop: "4px" }}>
          {matches.map((p) => (
            <div
              key={p.id}
              onClick={() => { onAdd(p); setQuery(""); }}
              style={{ padding: "6px 10px", cursor: "pointer", borderBottom: "1px solid #eee" }}
            >
              {p.plot_number}{p.owner_name ? ` — ${p.owner_name}` : " — unowned"}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function RegistrationRequests() {

  const [requests, setRequests] = useState([]);
  const [allPlots, setAllPlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [statusFilter, setStatusFilter] = useState("pending");
  const [busyId, setBusyId] = useState(null);
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  // Which request's row is showing the plot-override picker, and which
  // plot(s) are currently selected in it (empty selection = fall back to
  // the automatic claimed_plot_number match on approve).
  const [pickingId, setPickingId] = useState(null);
  const [pickedPlots, setPickedPlots] = useState([]);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const [requestData, plotData] = await Promise.all([fetchRegistrationRequests(), fetchAssignedPlots()]);
      setRequests(requestData);
      setAllPlots(plotData);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = statusFilter ? requests.filter((r) => r.status === statusFilter) : requests;
  const countByStatus = (status) => requests.filter((r) => r.status === status).length;

  // plotIdsOverride is only passed once the admin has actively picked
  // plot(s) in the picker — approving straight from the row (no picker
  // open) still uses the automatic claimed_plot_number match, same as before.
  const handleApprove = async (req, plotIdsOverride) => {
    if ((!plotIdsOverride || plotIdsOverride.length === 0) && !req.matched_plot_id) {
      setError(`No plot found matching "${req.claimed_plot_number}" — pick a plot manually, or reject this request instead.`);
      return;
    }
    setBusyId(req.id);
    setError("");
    try {
      const updated = await approveRegistrationRequest(req.id, plotIdsOverride);
      setRequests((prev) => prev.map((r) => (r.id === req.id ? updated : r)));
      setPickingId(null);
      setPickedPlots([]);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  const startPickPlot = (req) => {
    setPickingId(req.id);
    setPickedPlots([]);
    setRejectingId(null);
  };

  // Opens the picker pre-filled with every plot Zoho's own sync already
  // linked to this person's mobile/email (req.mobile_matched_plots) — the
  // admin can still remove/add before approving, same review step as
  // picking manually, just starting from the right set instead of empty.
  const useMatchedPlots = (req) => {
    const matchedIds = new Set((req.mobile_matched_plots || []).map((p) => p.id));
    const fullPlots = allPlots.filter((p) => matchedIds.has(p.id));
    setPickingId(req.id);
    setPickedPlots(fullPlots);
    setRejectingId(null);
  };

  const startReject = (req) => {
    setRejectingId(req.id);
    setRejectReason("");
    setPickingId(null);
    setPickedPlots([]);
  };

  const submitReject = async (req) => {
    if (!rejectReason.trim()) {
      setError("Please enter a reason for rejecting this request.");
      return;
    }
    setBusyId(req.id);
    setError("");
    try {
      const updated = await rejectRegistrationRequest(req.id, rejectReason.trim());
      setRequests((prev) => prev.map((r) => (r.id === req.id ? updated : r)));
      setRejectingId(null);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  return (

    <div>

      <Navbar />

      <Sidebar />

      <div className="admin-content">

        <div className="service-header">
          <h1 style={{ marginTop: 0, marginBottom: 0 }}>
            Registration Requests
          </h1>
          <div className="service-count">
            {countByStatus("pending")}
          </div>
        </div>

        {error && <p className="login-error" style={{ color: "red" }}>{error}</p>}

        <div className="filter-row">
          <button className={statusFilter === "pending" ? "active-filter" : ""} onClick={() => setStatusFilter("pending")}>
            Pending ({countByStatus("pending")})
          </button>
          <button className={statusFilter === "approved" ? "active-filter" : ""} onClick={() => setStatusFilter("approved")}>
            Approved ({countByStatus("approved")})
          </button>
          <button className={statusFilter === "rejected" ? "active-filter" : ""} onClick={() => setStatusFilter("rejected")}>
            Rejected ({countByStatus("rejected")})
          </button>
          <button className={statusFilter === "" ? "active-filter" : ""} onClick={() => setStatusFilter("")}>
            All ({requests.length})
          </button>
        </div>

        {loading && <p>Loading…</p>}
        {!loading && filtered.length === 0 && <p>No registration requests in this category.</p>}

        {!loading && filtered.length > 0 && (
          <div className="table-card">
            <table className="service-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Mobile</th>
                  <th>Claimed Plot</th>
                  <th>Matched Plot</th>
                  <th>Zoho-Synced Plots</th>
                  <th>Submitted</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((req) => (
                  <tr key={req.id}>
                    <td>{req.full_name}</td>
                    <td>{req.mobile}</td>
                    <td>{req.claimed_plot_number}</td>
                    <td>
                      {req.matched_plot_id ? (
                        <span>
                          Found
                          {req.matched_plot_owner_name
                            ? ` — currently owned by ${req.matched_plot_owner_name} (${req.matched_plot_owner_mobile})`
                            : " — unowned"}
                        </span>
                      ) : (
                        <span style={{ color: "#991b1b" }}>No matching plot</span>
                      )}
                    </td>
                    <td>
                      {req.mobile_matched_plots?.length > 0 ? (
                        <div>
                          <span>{req.mobile_matched_plots.map((p) => p.plot_number).join(", ")}</span>
                          {req.status === "pending" && (
                            <div>
                              <button
                                type="button"
                                style={{ marginTop: "4px" }}
                                disabled={busyId === req.id}
                                onClick={() => useMatchedPlots(req)}
                              >
                                Use these plot(s)
                              </button>
                            </div>
                          )}
                        </div>
                      ) : (
                        <span style={{ color: "#888" }}>—</span>
                      )}
                    </td>
                    <td>{new Date(req.created_at).toLocaleDateString()}</td>
                    <td>
                      <span className={STATUS_CLASS[req.status]}>{STATUS_LABELS[req.status]}</span>
                      {req.status === "rejected" && req.rejection_reason && (
                        <p style={{ fontSize: "12px", color: "#666", margin: "4px 0 0" }}>{req.rejection_reason}</p>
                      )}
                      {req.status !== "pending" && req.reviewed_by_name && (
                        <p style={{ fontSize: "12px", color: "#666", margin: "4px 0 0" }}>
                          {req.status === "approved" ? "Approved" : "Rejected"} by {req.reviewed_by_name}
                          {req.reviewed_at ? ` on ${new Date(req.reviewed_at).toLocaleDateString()}` : ""}
                        </p>
                      )}
                    </td>
                    <td>
                      {req.status === "pending" && rejectingId !== req.id && pickingId !== req.id && (
                        <div style={{ display: "flex", gap: "6px" }}>
                          <button className="progress-btn" disabled={busyId === req.id} onClick={() => handleApprove(req)}>
                            {busyId === req.id ? "Approving…" : "Approve"}
                          </button>
                          <button disabled={busyId === req.id} onClick={() => startPickPlot(req)}>
                            Pick Plot(s)…
                          </button>
                          <button className="close-btn" disabled={busyId === req.id} onClick={() => startReject(req)}>
                            Reject
                          </button>
                        </div>
                      )}
                      {req.status === "pending" && pickingId === req.id && (
                        <div style={{ display: "flex", flexDirection: "column", gap: "6px", minWidth: "220px" }}>
                          <PlotPicker
                            allPlots={allPlots}
                            selectedPlots={pickedPlots}
                            onAdd={(p) => setPickedPlots((prev) => [...prev, p])}
                            onRemove={(id) => setPickedPlots((prev) => prev.filter((p) => p.id !== id))}
                          />
                          <div style={{ display: "flex", gap: "6px" }}>
                            <button
                              className="progress-btn"
                              disabled={busyId === req.id || pickedPlots.length === 0}
                              onClick={() => handleApprove(req, pickedPlots.map((p) => p.id))}
                            >
                              {busyId === req.id
                                ? "Approving…"
                                : `Approve with ${pickedPlots.length > 1 ? `these ${pickedPlots.length} plots` : "this plot"}`}
                            </button>
                            <button onClick={() => { setPickingId(null); setPickedPlots([]); }}>Cancel</button>
                          </div>
                        </div>
                      )}
                      {req.status === "pending" && rejectingId === req.id && (
                        <div style={{ display: "flex", flexDirection: "column", gap: "6px", minWidth: "200px" }}>
                          <textarea
                            rows="2"
                            placeholder="Reason for rejection…"
                            value={rejectReason}
                            onChange={(e) => setRejectReason(e.target.value)}
                          />
                          <div style={{ display: "flex", gap: "6px" }}>
                            <button className="close-btn" disabled={busyId === req.id} onClick={() => submitReject(req)}>
                              {busyId === req.id ? "Rejecting…" : "Confirm Reject"}
                            </button>
                            <button onClick={() => setRejectingId(null)}>Cancel</button>
                          </div>
                        </div>
                      )}
                      {req.status !== "pending" && "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      </div>

    </div>

  );

}

export default RegistrationRequests;
